import { type Object3D, type Raycaster, Vector3 } from 'three';

import {
  KEY_POSE_FK_JOINT_IDS,
  resolveKeyPoseFkJointObject,
} from '../fk/keyPoseFkConfig';
import type { KeyPoseFkJointId } from '../types';
import type { MotionEditorBonePickerSkeletonType } from './types';

export interface PoseBoneProxyPickEntry {
  endObject: Object3D | null;
  endWorldPosition: Vector3;
  jointId: KeyPoseFkJointId;
  radiusScale: number;
  sourceBoneName: string;
  startObject: Object3D;
  startWorldPosition: Vector3;
}

export interface PoseBoneProxyIntersection {
  distance: number;
  distanceToProxySq: number;
  jointId: KeyPoseFkJointId;
  point: Vector3;
  sourceBoneName: string;
}

const BODY_SIZE_FALLBACK = 1;
const BASE_RADIUS_SCALE = 0.05;
const SPHERE_SEGMENT_LENGTH_EPSILON = 1e-5;

const scratch = {
  pointOnRay: new Vector3(),
  pointOnProxy: new Vector3(),
};

const SEGMENT_END_JOINT_CANDIDATES_BY_ID: Partial<
  Record<KeyPoseFkJointId, readonly KeyPoseFkJointId[]>
> = {
  pelvis: ['spine0', 'spine1'],
  spine0: ['spine1'],
  spine1: ['spine2'],
  spine2: ['spine3'],
  spine3: ['neck'],
  neck: ['head'],
  left_collar: ['left_shoulder'],
  right_collar: ['right_shoulder'],
  left_shoulder: ['left_elbow'],
  right_shoulder: ['right_elbow'],
  left_elbow: ['left_wrist'],
  right_elbow: ['right_wrist'],
  left_hip: ['left_knee'],
  right_hip: ['right_knee'],
  left_knee: ['left_ankle'],
  right_knee: ['right_ankle'],
  left_ankle: ['left_foot'],
  right_ankle: ['right_foot'],
};

const PROXY_RADIUS_SCALE_BY_JOINT_ID: Record<KeyPoseFkJointId, number> = {
  head: 2.1,
  neck: 1.3,
  spine3: 1.7,
  spine2: 1.7,
  spine1: 1.7,
  spine0: 1.7,
  pelvis: 2,
  left_collar: 1.1,
  right_collar: 1.1,
  left_shoulder: 1.25,
  right_shoulder: 1.25,
  left_elbow: 1.2,
  right_elbow: 1.2,
  left_wrist: 1.45,
  right_wrist: 1.45,
  left_hip: 1.35,
  right_hip: 1.35,
  left_knee: 1.25,
  right_knee: 1.25,
  left_ankle: 1.3,
  right_ankle: 1.3,
  left_foot: 1.55,
  right_foot: 1.55,
};

export function buildPoseBoneProxyPickEntries(
  scene: Object3D | null,
  skeletonType: MotionEditorBonePickerSkeletonType,
) {
  if (!scene) return [] as PoseBoneProxyPickEntry[];

  const objectByJointId = new Map<KeyPoseFkJointId, Object3D>();
  for (const jointId of KEY_POSE_FK_JOINT_IDS) {
    if (skeletonType === 'standard' && jointId === 'spine0') {
      continue;
    }

    const object = resolveKeyPoseFkJointObject(scene, jointId);
    if (object) {
      objectByJointId.set(jointId, object);
    }
  }

  const entries: PoseBoneProxyPickEntry[] = [];
  for (const [jointId, startObject] of objectByJointId) {
    entries.push({
      endObject: resolveSegmentEndObject(jointId, startObject, objectByJointId),
      endWorldPosition: new Vector3(),
      jointId,
      radiusScale: PROXY_RADIUS_SCALE_BY_JOINT_ID[jointId],
      sourceBoneName: startObject.name || jointId,
      startObject,
      startWorldPosition: new Vector3(),
    });
  }

  return entries;
}

export function intersectPoseBoneProxyPickEntries(
  entries: readonly PoseBoneProxyPickEntry[],
  raycaster: Raycaster,
) {
  return getPoseBoneProxyPickCandidates(entries, raycaster, 1)[0] ?? null;
}

export function getPoseBoneProxyPickCandidates(
  entries: readonly PoseBoneProxyPickEntry[],
  raycaster: Raycaster,
  maxCandidates: number,
) {
  if (entries.length === 0) return [];

  const bodySize = updateEntryWorldPositionsAndGetBodySize(entries);
  const baseRadius = bodySize * BASE_RADIUS_SCALE;
  const candidates: PoseBoneProxyIntersection[] = [];

  for (const entry of entries) {
    if (!isObjectVisible(entry.startObject)) continue;

    const hit = intersectPoseBoneProxyEntry(entry, raycaster, baseRadius);
    if (!hit) continue;

    candidates.push({
      distance: hit.distance,
      distanceToProxySq: hit.distanceToProxySq,
      jointId: entry.jointId,
      point: scratch.pointOnProxy.clone(),
      sourceBoneName: entry.sourceBoneName,
    });
  }

  candidates.sort(comparePoseBoneProxyCandidates);

  return candidates.slice(0, Math.max(Math.floor(maxCandidates), 0));
}

function resolveSegmentEndObject(
  jointId: KeyPoseFkJointId,
  startObject: Object3D,
  objectByJointId: ReadonlyMap<KeyPoseFkJointId, Object3D>,
) {
  const endJointIds = SEGMENT_END_JOINT_CANDIDATES_BY_ID[jointId];
  if (!endJointIds) return null;

  for (const endJointId of endJointIds) {
    const endObject = objectByJointId.get(endJointId);
    if (endObject && endObject !== startObject) {
      return endObject;
    }
  }

  return null;
}

function updateEntryWorldPositionsAndGetBodySize(
  entries: readonly PoseBoneProxyPickEntry[],
) {
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;

  for (const entry of entries) {
    entry.startObject.getWorldPosition(entry.startWorldPosition);
    includePointInBounds(entry.startWorldPosition);

    if (entry.endObject) {
      entry.endObject.getWorldPosition(entry.endWorldPosition);
    } else {
      entry.endWorldPosition.copy(entry.startWorldPosition);
    }
    includePointInBounds(entry.endWorldPosition);
  }

  const sizeX = maxX - minX;
  const sizeY = maxY - minY;
  const sizeZ = maxZ - minZ;
  const bodySize = Math.max(sizeX, sizeY, sizeZ);

  return Number.isFinite(bodySize) && bodySize > 0
    ? bodySize
    : BODY_SIZE_FALLBACK;

  function includePointInBounds(point: Vector3) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    minZ = Math.min(minZ, point.z);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
    maxZ = Math.max(maxZ, point.z);
  }
}

function intersectPoseBoneProxyEntry(
  entry: PoseBoneProxyPickEntry,
  raycaster: Raycaster,
  baseRadius: number,
) {
  const radius = baseRadius * entry.radiusScale;
  const radiusSq = radius * radius;
  const segmentLengthSq = entry.startWorldPosition.distanceToSquared(
    entry.endWorldPosition,
  );

  const distanceToProxySq =
    segmentLengthSq <= SPHERE_SEGMENT_LENGTH_EPSILON
      ? distanceSqToSphereProxy(entry, raycaster)
      : raycaster.ray.distanceSqToSegment(
          entry.startWorldPosition,
          entry.endWorldPosition,
          scratch.pointOnRay,
          scratch.pointOnProxy,
        );
  if (distanceToProxySq > radiusSq) return null;

  const distance = getDistanceAlongRay(raycaster);
  if (distance < raycaster.near || distance > raycaster.far) return null;

  return { distance, distanceToProxySq };
}

function distanceSqToSphereProxy(
  entry: PoseBoneProxyPickEntry,
  raycaster: Raycaster,
) {
  raycaster.ray.closestPointToPoint(
    entry.startWorldPosition,
    scratch.pointOnRay,
  );
  scratch.pointOnProxy.copy(entry.startWorldPosition);

  return scratch.pointOnRay.distanceToSquared(entry.startWorldPosition);
}

function getDistanceAlongRay(raycaster: Raycaster) {
  return (
    (scratch.pointOnRay.x - raycaster.ray.origin.x) *
      raycaster.ray.direction.x +
    (scratch.pointOnRay.y - raycaster.ray.origin.y) *
      raycaster.ray.direction.y +
    (scratch.pointOnRay.z - raycaster.ray.origin.z) * raycaster.ray.direction.z
  );
}

function comparePoseBoneProxyCandidates(
  left: PoseBoneProxyIntersection,
  right: PoseBoneProxyIntersection,
) {
  if (left.distanceToProxySq !== right.distanceToProxySq) {
    return left.distanceToProxySq - right.distanceToProxySq;
  }

  return left.distance - right.distance;
}

function isObjectVisible(object: Object3D) {
  let current: Object3D | null = object;
  while (current) {
    if (!current.visible) return false;
    current = current.parent;
  }

  return true;
}
